import { useEffect } from 'react';
import { stopActivePlayback } from './audio-answer-player';
import { useAudioAnswerStore } from './audio-answer-store';

/**
 * FRONT-2387: stops any answer currently reading itself out loud when the
 * chat surface that mounts this hook goes away — the desktop chat modal
 * closing, the phone shell being left — or when the session it's showing
 * changes under it (switching threads without unmounting). Starting a new
 * playback already stops whatever else was playing (`speak()` in
 * `audio-answer-player.ts` does that itself); this is the other half —
 * audio must not keep going once nobody could still be looking at the
 * message it belongs to.
 *
 * Deliberately tiny and side-effect-only: no store reads, no rendering. Every
 * surface that renders the shared message list (`chat-messages.tsx`) and so
 * gets the play-answer control mounts exactly one of these, at its own top
 * level — never inside a per-message component, which would fire this on
 * every message mount/unmount instead of once per surface. As of FRONT-2387
 * fix round 4 that's four call sites: `inbox-chat-modal.tsx` (desktop),
 * `pwa-shell.tsx` (phone), `operator-chat-panel.tsx` (the operator run
 * panel's Chat tab) and `day-one-chat.tsx` (onboarding).
 */
export function useStopAudioAnswerOnSurfaceChange(sessionId: string | undefined): void {
  useEffect(() => {
    return () => {
      stopActivePlayback();
      useAudioAnswerStore.getState().clearPlaying();
    };
    // Re-running (which is what stops/clears) only on a session change or
    // final unmount — never on some unrelated re-render of the surface.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
}
